import React, { useState, useRef, useEffect } from 'react';
import { ActionButton, TooltipTrigger, Tooltip } from '@adobe/react-spectrum';
import { useDispatch } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { DropdownChevronIcon } from './DropdownChevronIcon';
import { normalizeRiskLevelToStored, RISK_LEVELS } from 'utils/constants';
import styles from './TimelineCard.module.scss';

interface RiskLevelProps {
  riskLevel?: string;
  issueId?: string;
  className?: string;
  isDateClicked?: boolean;
  isDropdownOpen?: boolean; // ✅ Controlled state from parent
  onDropdownToggle?: () => void; // ✅ Callback to toggle dropdown
}

export const RiskLevel: React.FC<RiskLevelProps> = ({
  riskLevel,
  issueId,
  className = '',
  isDateClicked = false,
  isDropdownOpen: externalDropdownOpen, // ✅ Controlled state from parent
  onDropdownToggle // ✅ Callback to toggle dropdown
}) => {
  // ✅ Use controlled state if provided, otherwise use internal state
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);
  const isDropdownOpen =
    externalDropdownOpen !== undefined
      ? externalDropdownOpen
      : internalDropdownOpen;

  const [currentRiskLevel, setCurrentRiskLevel] = useState(riskLevel);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();

  // Update local state when prop changes
  useEffect(() => {
    setCurrentRiskLevel(riskLevel);
  }, [riskLevel, isDateClicked]);

  const normalizedCurrentRiskLevel =
    normalizeRiskLevelToStored(currentRiskLevel) ?? currentRiskLevel;

  // Exact same logic as getStatusIcon() from TimelineCard
  const getStatusIcon = (level?: string) => {
    const size = 20;
    const riskLevelToUse =
      normalizeRiskLevelToStored(level ?? currentRiskLevel) ??
      level ??
      currentRiskLevel;
    if (riskLevelToUse === RISK_LEVELS.LOW) {
      return <LegendRiskLowIcon width={size} height={size} />;
    } else if (riskLevelToUse === RISK_LEVELS.MODERATE) {
      return <LegendRiskModerateIcon width={size} height={size} />;
    } else if (riskLevelToUse === RISK_LEVELS.HIGH) {
      return <LegendRiskHighIcon width={size} height={size} />;
    } else if (riskLevelToUse === RISK_LEVELS.IN_PLANNING) {
      return <LegendInPlanningIcon width={16} height={16} />;
    }
    return null;
  };

  // Get risk level display text for tooltip
  const getRiskLevelText = (level?: string): React.ReactNode => {
    const riskLevelToUse =
      normalizeRiskLevelToStored(level ?? currentRiskLevel) ??
      level ??
      currentRiskLevel;
    let riskText = 'Unknown';

    if (riskLevelToUse === RISK_LEVELS.LOW) {
      riskText = 'Low';
    } else if (riskLevelToUse === RISK_LEVELS.MODERATE) {
      riskText = 'Moderate';
    } else if (riskLevelToUse === RISK_LEVELS.HIGH) {
      riskText = 'High';
    } else if (riskLevelToUse === RISK_LEVELS.IN_PLANNING) {
      riskText = 'In Planning';
    }

    // For "In Planning", show without "Risk Level:" prefix and without bold
    if (riskLevelToUse === RISK_LEVELS.IN_PLANNING) {
      return <>{riskText}</>;
    }

    return (
      <>
        Risk Level: <span style={{ fontWeight: 'bold' }}>{riskText}</span>
      </>
    );
  };

  const handleStatusClick = () => {
    if (isDateClicked) {
      if (onDropdownToggle) {
        onDropdownToggle(); // Use parent callback if controlled
      } else {
        setInternalDropdownOpen(prev => !prev); // Toggle internal state if not controlled
      }
    }
  };

  const handleRiskLevelSelect = (
    newLevel: string,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setCurrentRiskLevel(newLevel);
    dispatch(
      actions.updateTooltipDetails({
        riskLevel: newLevel
      })
    );

    // Close the dropdown after selection - use parent callback if controlled
    if (onDropdownToggle && isDropdownOpen) {
      onDropdownToggle();
    } else {
      setInternalDropdownOpen(false);
    }
  };

  // Risk level options for dropdown
  const riskLevelOptions = [
    { value: RISK_LEVELS.LOW, label: 'Low' },
    { value: RISK_LEVELS.MODERATE, label: 'Moderate' },
    { value: RISK_LEVELS.HIGH, label: 'High' },
    { value: RISK_LEVELS.IN_PLANNING, label: 'In Planning' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (onDropdownToggle && isDropdownOpen) {
          onDropdownToggle(); // Close via parent if controlled
        } else {
          setInternalDropdownOpen(false); // Close internal state if not controlled
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, onDropdownToggle]);

  const displayableRiskLevels = new Set<string>([
    RISK_LEVELS.HIGH,
    RISK_LEVELS.MODERATE,
    RISK_LEVELS.LOW,
    RISK_LEVELS.IN_PLANNING
  ]);

  // Only show if risk level exists and is not NA
  if (
    !normalizedCurrentRiskLevel ||
    !displayableRiskLevels.has(normalizedCurrentRiskLevel)
  ) {
    return null;
  }

  return (
    <div className={`${styles.riskLevelContainer} ${className}`}>
      {isDropdownOpen ? (
        // Show current risk level icon AND dropdown when editing
        <>
          <TooltipTrigger placement="top">
            <ActionButton
              isQuiet
              onPress={handleStatusClick}
              UNSAFE_className={styles.riskLevelDisplay}
            >
              <span className={styles.riskLevelIcon}>{getStatusIcon()}</span>
              <DropdownChevronIcon />
            </ActionButton>
            <Tooltip>{getRiskLevelText()}</Tooltip>
          </TooltipTrigger>
          <div ref={dropdownRef} className={styles.riskLevelDropdown}>
            {riskLevelOptions.map(option => (
              <div
                key={option.value}
                className={styles.riskLevelMenuItem}
                onMouseDown={e => handleRiskLevelSelect(option.value, e)}
              >
                <div className={styles.riskLevelMenuItemContent}>
                  <span className={styles.riskIcon}>
                    {getStatusIcon(option.value)}
                  </span>
                  <span className={styles.riskLevelMenuItemText}>
                    {option.value === RISK_LEVELS.IN_PLANNING ? (
                      option.label
                    ) : (
                      <>
                        Risk Level:{' '}
                        <span className={styles.riskLevelLabel}>
                          <strong>{option.label}</strong>
                        </span>
                      </>
                    )}
                  </span>
                </div>
                {normalizedCurrentRiskLevel === option.value && (
                  <BlackCheckMarkIcon
                    width={18}
                    height={16}
                    className={styles.checkmarkRisk}
                  />
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        // Show clickable status (like EditTitle)
        <TooltipTrigger placement="top">
          <ActionButton
            isQuiet
            onPress={handleStatusClick}
            UNSAFE_className={`${styles.riskLevelDisplay} ${
              !isDateClicked ? styles.riskLevelReadOnly : ''
            }`}
            aria-label={`Risk Level: ${currentRiskLevel}. Click to edit.`}
          >
            <span className={styles.riskLevelIcon}>{getStatusIcon()}</span>
            {isDateClicked && <DropdownChevronIcon />}
          </ActionButton>
          <Tooltip>{getRiskLevelText()}</Tooltip>
        </TooltipTrigger>
      )}
    </div>
  );
};
